import { useState } from "react";
import { Mail, Palette, Plus, Trash2 } from "lucide-react";
import { CategorySettingsDialog } from "@/components/CategorySettingsDialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

export const CategoryManager = ({ cats, onChange }) => {
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/categories", { name });
      setName("");
      onChange();
      toast.success("Category added");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/categories/${c.id}`);
      onChange();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-none bg-transparent" data-testid="manage-categories-btn">Manage categories</Button>
      </DialogTrigger>
      <DialogContent className="bg-[#FFFDF9] rounded-none sm:max-w-2xl" data-testid="category-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-normal">Categories</DialogTitle>
        </DialogHeader>
        <ul className="divide-y divide-[#2A2325]/10 border-y border-[#2A2325]/10 max-h-[60vh] overflow-y-auto">
          {cats.map((c) => (
            <li key={c.id} className="py-3 text-sm" data-testid={`category-row-${c.id}`}>
              <div className="flex items-center justify-between">
              <span className="font-body-serif text-base flex items-center gap-2">
                <span className="w-3 h-3 inline-block" style={{ background: c.theme?.dark }} />
                <span className="w-3 h-3 inline-block" style={{ background: c.theme?.accent }} />
                {c.name}
                <span className="text-xs text-[#5C5255] font-sans">{c.theme?.name}</span>
              </span>
              <span className="flex items-center gap-3 text-[#5C5255]">
                {c.product_count} {c.product_count === 1 ? "title" : "titles"}
                <button onClick={() => setEditing({ c, tab: "note" })} className={`hover:text-[#5E2B38] ${c.thank_you ? "text-[#2D5A3F]" : ""}`} data-testid={`edit-thank-you-${c.id}`} aria-label="Thank-you note" title={c.thank_you ? "Thank-you note set" : "Add a thank-you note"}><Mail size={14} /></button>
                <button onClick={() => setEditing({ c, tab: "colours" })} className="hover:text-[#5E2B38] flex items-center gap-1 text-xs border border-[#2A2325]/15 px-2 py-1" data-testid={`edit-theme-${c.id}`} aria-label="Colours & settings"><Palette size={13} /> Colours & settings</button>
                <button onClick={() => remove(c)} className="hover:text-[#9B2C2C]" data-testid={`delete-category-${c.id}`} aria-label="Delete category">
                  <Trash2 size={14} />
                </button>
              </span>
              </div>
            </li>
          ))}
        </ul>
        <CategorySettingsDialog key={editing ? `${editing.c.id}-${editing.tab}` : "none"} cat={editing?.c} tab={editing?.tab} open={!!editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={onChange} />
        <form onSubmit={add} className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category, e.g. Poetry" data-testid="new-category-input" />
          <Button type="submit" className="rounded-none gap-1" data-testid="add-category-btn"><Plus size={14} /> Add</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
